import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

// Errors that are caused by Google Translate (or other extensions) mutating
// the DOM out from under React. These are transient — re-rendering the tree
// fixes them — so we silently self-heal instead of showing the error screen.
const TRANSIENT_PATTERNS = [
  'removeChild', 'insertBefore', 'replaceChild', 'appendChild',
  'NotFoundError', 'The node to be removed', 'The node before which',
];

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, retries: 0 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const msg = `${error?.name || ''} ${error?.message || ''}`;
    const transient = TRANSIENT_PATTERNS.some((p) => msg.includes(p));
    // Auto-recover from Google-Translate DOM glitches (up to a few times) so
    // navigation / password change / any page doesn't dump the user onto the
    // error screen. A hard, repeating error still surfaces normally.
    if (transient && this.state.retries < 4) {
      setTimeout(() => {
        this.setState((s) => ({ hasError: false, error: null, errorInfo: null, retries: s.retries + 1 }));
      }, 0);
      return;
    }
    this.setState({ errorInfo });
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, retries: 0 });
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" translate="no">
          <Card className="max-w-lg w-full">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <CardTitle className="text-xl font-heading text-slate-900">
                Something went wrong
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-center text-slate-600">
                We hit an unexpected error on this page. Please try again or return to the dashboard.
                <br />
                <span className="text-slate-500 text-sm">
                  Ocurrió un error inesperado en esta página. Inténtalo de nuevo o vuelve al panel.
                </span>
              </p>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="bg-slate-100 p-3 rounded-lg overflow-auto max-h-40">
                  <p className="text-xs font-mono text-red-600 whitespace-pre-wrap">
                    {this.state.error.toString()}
                  </p>
                </div>
              )}

              <div className="flex gap-3 justify-center pt-2">
                <Button variant="outline" onClick={this.handleGoHome}>
                  <Home size={16} className="mr-2" />
                  Go to Dashboard
                </Button>
                <Button onClick={this.handleRetry}>
                  <RefreshCw size={16} className="mr-2" />
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
